import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Checkbox,
  Chip,
  FormControlLabel,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { Payment as PaymentIcon, FileDownload as FileDownloadIcon, ListAlt as ListAltIcon } from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { formatAmount, formatDateOnly, getFormatConfig, initializeFormats } from '../../../utils/global';
import { useApHub } from '../ApHub/ApHubContext';
import ApHubBreadcrumb from '../ApHub/ApHubBreadcrumb';
import { downloadPaymentBatchExcel } from './paymentBatchExcelExport';
import PaymentProposalDrawer from './PaymentProposalDrawer';
import {
  enrichConfigsWithBalances,
  buildBatchBanksPayload,
  collectBatchPaymentLines,
  formatMoneyInput,
  getSelectedRowsMissingProposals,
  groupBatchPaymentLinesByBank,
  groupBatchPaymentLinesByVendorAndBank,
  loadProposedPaymentConfigs,
  resolveBatchHeaderPaymentMethod,
  roundMoney,
  upsertProposedPayment,
} from './paymentProposalUtils';
import {
  acknowledgeDuplicateWarning,
  dismissDuplicateWarning,
  fetchDuplicateWarnings,
  voidDuplicatePayment,
  warningLabel,
  warningsForInvoice,
  warningsForPaymentIds,
} from './paymentDuplicateWarning';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateSectionHeaderSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateToolbarButtonSx,
  harmonateUploadOutlinedSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getTenantId = () => localStorage.getItem('tenant_id') || '';
const getUserId = () => localStorage.getItem('user_id') || null;
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const formatProposalPaymentStatus = (paymentStatus, paidAmount, balance, hasProposal) => {
  const normalized = (paymentStatus || '').toUpperCase();
  if (hasProposal && normalized !== 'PAID') return 'Proposed';
  if (normalized === 'PAID' || balance <= 0.01) return 'Paid';
  if (normalized === 'PARTIAL' || paidAmount > 0) return 'Partial';
  return 'Unpaid';
};

const paymentStatusChipProps = (label) => {
  if (label === 'Paid') return { color: 'success', variant: 'filled' };
  if (label === 'Proposed') return { color: 'warning', variant: 'outlined' };
  if (label === 'Partial') return { color: 'warning', variant: 'filled' };
  return { color: 'default', variant: 'outlined' };
};

const mapInvoiceToProposalRow = (inv, defaultBankId = '') => {
  const amount = roundMoney(inv.invoiceTotalAmountDue);
  const paidAmount = roundMoney(inv.invoicePaymentsReceived);
  const balance = inv.invoiceRemainingBalanceDue != null
    ? roundMoney(inv.invoiceRemainingBalanceDue)
    : roundMoney(Math.max(0, amount - paidAmount));

  return {
    apInvoiceId: inv.apInvoiceId,
    invoiceNumber: inv.invoiceNumber,
    vendorName: inv.vendorName,
    vendorId: inv.vendorId,
    businessUnitId: inv.businessUnitId,
    amount,
    paidAmount,
    balance,
    bankId: defaultBankId,
    paymentMethod: 'ACH',
    workflowStatus: inv.workflowStatus,
    paymentStatus: inv.paymentStatus,
    displayPaymentStatus: formatProposalPaymentStatus(inv.paymentStatus, paidAmount, balance),
    approveDate: (inv.workflowStatus || '').toUpperCase() === 'APPROVED' ? inv.updatedAt : null,
    expectedPayDate: inv.invoiceDueDate || null,
    raw: inv,
  };
};

const getBankDisplayName = (bank) => bank?.bankShortName || bank?.bankName || bank?.bankCode || '';

const PaymentProposal = () => {
  const { canAdd, canEdit, canExport } = useFunctionPermissions(FC.PAYMENT_INTELLIGENCE);
  const navigate = useNavigate();
  const tenantId = getTenantId();
  const config = getFormatConfig();
  const { toastState, showSuccess, showError, hideToast } = useToast();
  const { selectedBusinessUnitId, selectedBusinessUnitName } = useApHub();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [banks, setBanks] = useState([]);
  const [availableCash, setAvailableCash] = useState(null);
  const [selectionModel, setSelectionModel] = useState([]);
  const [paymentConfigs, setPaymentConfigs] = useState({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [createdBatchId, setCreatedBatchId] = useState(null);
  const [lastBatchCount, setLastBatchCount] = useState(0);
  const [lastBatchExport, setLastBatchExport] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [savingPaymentDetails, setSavingPaymentDetails] = useState(false);
  const [duplicateWarnings, setDuplicateWarnings] = useState([]);
  const [duplicateReviewed, setDuplicateReviewed] = useState(false);
  const [duplicateActionId, setDuplicateActionId] = useState(null);

  const loadDuplicateWarnings = useCallback(async () => {
    if (!tenantId) {
      setDuplicateWarnings([]);
      return;
    }
    const warnings = await fetchDuplicateWarnings(tenantId);
    setDuplicateWarnings(warnings);
  }, [tenantId]);

  const fetchPayableInvoices = useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (!tenantId) {
      setRows([]);
      if (!silent) setLoading(false);
      return;
    }
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      const [approvedInvoices, bankList] = await Promise.all([
        apiFetch(
          `conductor/ap-invoice-headers/by-workflow-status?tenant_id=${tenantId}&workflow_status=APPROVED`
        ),
        apiFetch(`conductor/cash-recon/banks/by-tenant-and-status?tenant_id=${tenantId}&status=ACTIVE`).catch(() => []),
      ]);

      const activeBanks = (Array.isArray(bankList) ? bankList : [])
        .filter((bank) => (bank.status || 'ACTIVE').toUpperCase() === 'ACTIVE');
      const defaultBankId = activeBanks[0]?.bankId || '';

      const approvedList = Array.isArray(approvedInvoices) ? approvedInvoices : [];

      const payable = approvedList
        .map((inv) => mapInvoiceToProposalRow(inv, defaultBankId))
        .filter((row) => row.balance > 0 && (row.paymentStatus || '').toUpperCase() !== 'PAID')
        .filter((row) => !selectedBusinessUnitId || row.businessUnitId === selectedBusinessUnitId);

      const savedConfigs = enrichConfigsWithBalances(
        await loadProposedPaymentConfigs(tenantId, payable.map((row) => row.apInvoiceId), payable),
        payable
      );

      const payableWithSaved = payable.map((row) => {
        const saved = savedConfigs[row.apInvoiceId];
        if (!saved) return row;
        return {
          ...row,
          bankId: saved.bankId || row.bankId,
          paymentMethod: saved.paymentMethod || row.paymentMethod,
        };
      });

      setBanks(activeBanks);
      setRows(payableWithSaved);
      setPaymentConfigs(savedConfigs);
      setAvailableCash(null);
      await loadDuplicateWarnings();
    } catch (err) {
      if (!silent) {
        setError(err.message || 'Failed to load approved invoices');
        showError(err.message || 'Failed to load approved invoices');
        setRows([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [tenantId, showError, selectedBusinessUnitId, loadDuplicateWarnings]);

  useEffect(() => {
    if (tenantId) initializeFormats(tenantId);
    fetchPayableInvoices();
  }, [fetchPayableInvoices, tenantId]);

  const payableRows = useMemo(
    () => rows.filter((r) => r.balance > 0),
    [rows]
  );

  const selectedRows = useMemo(
    () => payableRows.filter((r) => selectionModel.includes(r.apInvoiceId)),
    [payableRows, selectionModel]
  );

  const getProposedPay = useCallback((apInvoiceId) => {
    const config = paymentConfigs[apInvoiceId];
    if (!config?.saved) return 0;
    return Number(config.totalPayAmount ?? config.payAmount) || 0;
  }, [paymentConfigs]);

  const getDisplayPaidAmount = useCallback((row) => (
    Number(row.paidAmount) || 0
  ), []);

  const getDisplayBalance = useCallback((row) => (
    roundMoney(row.balance)
  ), []);

  const getDisplayPaymentStatus = useCallback((row) => {
    const hasProposal = getProposedPay(row.apInvoiceId) > 0.001;
    return formatProposalPaymentStatus(
      row.paymentStatus,
      getDisplayPaidAmount(row),
      getDisplayBalance(row),
      hasProposal,
    );
  }, [getProposedPay, getDisplayPaidAmount, getDisplayBalance]);

  const resolveBankName = useCallback((bankId) => {
    const bank = banks.find((item) => item.bankId === bankId);
    return getBankDisplayName(bank);
  }, [banks]);

  const batchPaymentLines = useMemo(
    () => collectBatchPaymentLines(selectedRows, paymentConfigs),
    [selectedRows, paymentConfigs]
  );

  const selectedDuplicateWarnings = useMemo(() => {
    const matches = warningsForPaymentIds(
      duplicateWarnings,
      batchPaymentLines.map((line) => line.paymentId),
    );
    const seen = new Set();
    return matches.filter((warning) => {
      const key = [String(warning.paymentId), String(warning.matchedPaymentId)].sort().join('|');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [duplicateWarnings, batchPaymentLines]);

  const batchPaymentGroups = useMemo(
    () => groupBatchPaymentLinesByBank(batchPaymentLines),
    [batchPaymentLines]
  );

  const batchVendorBankGroups = useMemo(
    () => groupBatchPaymentLinesByVendorAndBank(batchPaymentLines),
    [batchPaymentLines]
  );

  const selectedRowsMissingProposals = useMemo(
    () => getSelectedRowsMissingProposals(selectedRows, paymentConfigs),
    [selectedRows, paymentConfigs]
  );

  const drawerInitialForm = useMemo(() => {
    if (!activeRow) {
      return { payAmount: '', bankId: '', paymentMethod: 'ACH', paymentType: 'full' };
    }
    const saved = paymentConfigs[activeRow.apInvoiceId];
    const remainingBalance = saved?.remainingBalance ?? activeRow.balance;
    return {
      paymentType: 'full',
      payAmount: formatMoneyInput(remainingBalance),
      bankId: saved?.bankId || activeRow.bankId || banks[0]?.bankId || '',
      paymentMethod: saved?.paymentMethod || activeRow.paymentMethod || 'ACH',
    };
  }, [activeRow, paymentConfigs, banks]);

  const handleRowClick = (params, event) => {
    if (event?.target?.closest?.('.MuiCheckbox-root, .MuiDataGrid-cellCheckbox')) {
      return;
    }
    setActiveRow(params.row);
    setDrawerOpen(true);
  };

  const handleSavePaymentDetails = async ({ apInvoiceId, payAmount, bankId, paymentMethod, isPartial }) => {
    if (!canEdit) return;
    const row = rows.find((item) => item.apInvoiceId === apInvoiceId);
    if (!row || !tenantId) return;

    try {
      setSavingPaymentDetails(true);
      const userId = localStorage.getItem('user_id');
      const savedConfig = await upsertProposedPayment({
        tenantId,
        row,
        payAmount,
        bankId,
        paymentMethod,
        userId,
        isFullPayment: !isPartial,
      });

      setPaymentConfigs((prev) => ({
        ...prev,
        [apInvoiceId]: savedConfig,
      }));
      setRows((prev) => prev.map((item) => (
        item.apInvoiceId === apInvoiceId
          ? {
            ...item,
            bankId: bankId || '',
            paymentMethod,
          }
          : item
      )));
      showSuccess('Payment details saved');
      await loadDuplicateWarnings();
    } catch (err) {
      showError(err.message || 'Failed to save payment details');
    } finally {
      setSavingPaymentDetails(false);
    }
  };

  const totalPaymentAmount = useMemo(
    () => batchPaymentLines.reduce((sum, line) => sum + line.amount, 0),
    [batchPaymentLines]
  );

  const forecastedBalance = availableCash != null ? availableCash - totalPaymentAmount : null;

  const columns = useMemo(() => [
    { field: 'invoiceNumber', headerName: 'Invoice Number', width: 130 },
    { field: 'vendorName', headerName: 'Vendor', flex: 1, minWidth: 160 },
    {
      field: 'approveDate',
      headerName: 'Approve Date',
      width: 120,
      valueFormatter: (p) => (p.value ? formatDateOnly(p.value, config.DateFormat) : '—'),
    },
    {
      field: 'expectedPayDate',
      headerName: 'Expected Pay Date',
      width: 130,
      valueFormatter: (p) => (p.value ? formatDateOnly(p.value, config.DateFormat) : '—'),
    },
    {
      field: 'displayPaymentStatus',
      headerName: 'Paid',
      width: 100,
      valueGetter: (params) => getDisplayPaymentStatus(params.row),
      renderCell: (params) => {
        const label = getDisplayPaymentStatus(params.row);
        const chip = paymentStatusChipProps(label);
        return <Chip label={label} size="small" color={chip.color} variant={chip.variant} />;
      },
    },
    {
      field: 'duplicateWarning',
      headerName: 'Duplicate',
      width: 240,
      sortable: false,
      renderCell: (params) => {
        const rowWarnings = warningsForInvoice(duplicateWarnings, params.row.apInvoiceId);
        if (!rowWarnings.length) return '—';
        const warning = rowWarnings[0];
        const fullText = rowWarnings
          .map((item) => (
            item.matchedInvoiceNumber
              ? `${warningLabel(item)} (${item.matchedInvoiceNumber})`
              : warningLabel(item)
          ))
          .join('\n');
        return (
          <Tooltip title={fullText} placement="top" arrow>
            <Chip
              size="small"
              color="warning"
              label={warningLabel(warning)}
              sx={{ maxWidth: '100%' }}
              onClick={(event) => {
                event.stopPropagation();
                const match = rows.find((item) => item.apInvoiceId === warning.matchedApInvoiceId);
                if (match) {
                  setActiveRow(match);
                  setDrawerOpen(true);
                }
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (p) => formatAmount(p.value, config.AmountFormat),
    },
    {
      field: 'paidAmount',
      headerName: 'Paid Amount',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (params) => getDisplayPaidAmount(params.row),
      valueFormatter: (p) => formatAmount(p.value, config.AmountFormat),
    },
    {
      field: 'balance',
      headerName: 'Balance',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (params) => getDisplayBalance(params.row),
      valueFormatter: (p) => formatAmount(p.value, config.AmountFormat),
    },
  ], [
    config.AmountFormat,
    config.DateFormat,
    duplicateWarnings,
    rows,
    paymentConfigs,
    getDisplayPaidAmount,
    getDisplayBalance,
    getDisplayPaymentStatus,
  ]);

  const handleOpenConfirm = () => {
    if (!canAdd || !selectedRows.length) return;

    if (selectedRowsMissingProposals.length) {
      const invoiceList = selectedRowsMissingProposals
        .map((row) => row.invoiceNumber)
        .join(', ');
      showError(`Configure payment details in the drawer for: ${invoiceList}`);
      return;
    }

    if (!batchPaymentLines.length) {
      showError('No proposed payments found for the selected invoices');
      return;
    }

    setDuplicateReviewed(false);
    setConfirmDialogOpen(true);
  };

  const handleConfirmCreateBatch = async () => {
    if (!canAdd) return;
    if (selectedDuplicateWarnings.length && !duplicateReviewed) {
      showError('Review the possible duplicate warning before releasing these payments');
      return;
    }
    try {
      if (selectedDuplicateWarnings.length) {
        const userId = getUserId();
        const userName = localStorage.getItem('username') || localStorage.getItem('user_name') || '';
        await Promise.all(selectedDuplicateWarnings.map((warning) => acknowledgeDuplicateWarning({
          tenantId,
          paymentId: warning.paymentId,
          matchedPaymentId: warning.matchedPaymentId,
          userId: userId || null,
          userName,
          action: 'CONFIRM_RELEASE',
          comment: 'Reviewed possible duplicate warning before creating the payment batch',
        })));
      }
      setConfirmDialogOpen(false);
      handleCreateBatch();
    } catch (err) {
      showError(err.message || 'Failed to record the duplicate warning review');
    }
  };

  const handleDismissDuplicate = async (warning) => {
    if (!canEdit || !warning) return;
    try {
      setDuplicateActionId(warning.paymentId);
      await dismissDuplicateWarning({
        tenantId,
        paymentId: warning.paymentId,
        matchedPaymentId: warning.matchedPaymentId,
        userId: getUserId() || null,
        userName: localStorage.getItem('username') || localStorage.getItem('user_name') || '',
        comment: 'Marked as not a duplicate',
      });
      showSuccess('Warning removed for this payment pair');
      await loadDuplicateWarnings();
    } catch (err) {
      showError(err.message || 'Failed to dismiss the duplicate warning');
    } finally {
      setDuplicateActionId(null);
    }
  };

  const handleVoidDuplicate = async (warning) => {
    if (!canEdit || !warning) return;
    try {
      setDuplicateActionId(warning.paymentId);
      await voidDuplicatePayment({
        tenantId,
        paymentId: warning.paymentId,
        matchedPaymentId: warning.matchedPaymentId,
        userId: getUserId() || null,
        userName: localStorage.getItem('username') || localStorage.getItem('user_name') || '',
        action: 'VOID_DUPLICATE',
        comment: 'Voided as a duplicate',
      });
      showSuccess('Payment voided as a duplicate');
      await loadDuplicateWarnings();
      await fetchPayableInvoices({ silent: true });
    } catch (err) {
      showError(err.message || 'Failed to void the duplicate payment');
    } finally {
      setDuplicateActionId(null);
    }
  };

  const handleCreateBatch = async () => {
    if (!canAdd) return;
    if (!selectedRows.length || !tenantId || !batchPaymentLines.length) return;

    try {
      setSubmitting(true);
      const userId = localStorage.getItem('user_id');
      const batchNumber = `BATCH-${Date.now()}`;
      const today = new Date().toISOString().slice(0, 10);

      const payAmountByInvoice = batchPaymentLines.reduce((map, line) => {
        map[line.apInvoiceId] = (map[line.apInvoiceId] || 0) + line.amount;
        return map;
      }, {});

      const paymentsForExport = batchPaymentLines.map((line, index) => {
        const row = line.row;
        const balanceBefore = Number(row.balance) || 0;
        const priorPaid = Number(row.paidAmount) || 0;
        const batchPayForInvoice = payAmountByInvoice[line.apInvoiceId] || 0;
        const newPaid = priorPaid + batchPayForInvoice;
        const newBalance = Math.max(0, balanceBefore - batchPayForInvoice);

        return {
          invoiceNumber: line.invoiceNumber,
          vendorName: line.vendorName,
          amount: row.amount,
          paidAmount: priorPaid,
          paymentAmount: line.amount,
          remainingBalance: newBalance,
          bankName: resolveBankName(line.bankId),
          paymentMethod: line.paymentMethod,
          paymentReference: `${batchNumber}-${line.invoiceNumber}-${index + 1}`,
        };
      });

      const firstLine = batchPaymentLines[0];
      const batchBankId = batchPaymentGroups.length === 1
        ? batchPaymentGroups[0]?.bankId || null
        : null;
      const batchPaymentMethod = resolveBatchHeaderPaymentMethod(batchPaymentGroups);

      const batchBanksPayload = buildBatchBanksPayload(batchPaymentGroups, batchVendorBankGroups);

      const batch = await apiFetch('conductor/payment-batches', {
        method: 'POST',
        body: JSON.stringify({
          tenantId,
          batchNumber,
          bankId: batchBankId,
          paymentMethod: batchPaymentMethod,
          totalAmount: totalPaymentAmount,
          batchStatus: 'DRAFT',
          paymentDate: today,
          businessUnitId: selectedRows[0]?.businessUnitId || null,
          createdBy: userId || null,
          banks: batchBanksPayload,
        }),
      });

      const processedInvoices = new Set();

      await Promise.all(batchPaymentLines.map(async (line, index) => {
        const row = line.row;
        const existingTransaction = await apiFetch(`conductor/payment-transactions/${line.paymentId}`);
        const updatedTransaction = await apiFetch(`conductor/payment-transactions/${line.paymentId}`, {
          method: 'PUT',
          body: JSON.stringify({
            ...existingTransaction,
            paymentBatchId: batch.paymentBatchId,
            vendorId: row.vendorId,
            bankId: line.bankId || null,
            paymentReference: `${batchNumber}-${line.invoiceNumber}-${index + 1}`,
            paymentAmount: line.amount,
            paymentMethod: line.paymentMethod,
            paymentStatus: 'PENDING',
            updatedBy: userId || null,
          }),
        });

        if (line.mappingId) {
          const existingMapping = await apiFetch(`conductor/invoice-payment-mappings/${line.mappingId}`);
          await apiFetch(`conductor/invoice-payment-mappings/${line.mappingId}`, {
            method: 'PUT',
            body: JSON.stringify({
              ...existingMapping,
              bankId: updatedTransaction.bankId || line.bankId || null,
              paidAmount: line.amount,
              mappingDate: today,
              updatedBy: userId || null,
            }),
          });
        }
      }));

      await Promise.all(selectedRows.map(async (row) => {
        if (processedInvoices.has(row.apInvoiceId)) return;
        processedInvoices.add(row.apInvoiceId);

        const batchPayAmount = payAmountByInvoice[row.apInvoiceId] || 0;
        const balanceBefore = Number(row.balance) || 0;
        const priorPaid = Number(row.paidAmount) || 0;
        const newPaid = priorPaid + batchPayAmount;
        const newBalance = Math.max(0, balanceBefore - batchPayAmount);
        const paymentStatus = newBalance <= 0.01 ? 'PAID' : 'PARTIAL';

        await apiFetch(`conductor/ap-invoice-headers/${row.apInvoiceId}`, {
          method: 'PUT',
          body: JSON.stringify({
            ...row.raw,
            invoicePaymentsReceived: newPaid,
            invoiceRemainingBalanceDue: newBalance,
            paymentStatus,
            updatedBy: userId || null,
          }),
        });
      }));

      const bankSummary = batchVendorBankGroups.flatMap((vendor) => (
        vendor.bankGroups.map((group) => ({
          vendorName: vendor.vendorName,
          bankName: resolveBankName(group.bankId),
          paymentMethods: group.paymentMethodLabel,
          paymentCount: group.paymentCount,
          totalAmount: group.amount,
        }))
      ));

      const exportPayload = {
        batch: {
          batchNumber: batch.batchNumber || batchNumber,
          paymentDate: batch.paymentDate || today,
          bankName: batchPaymentGroups.length === 1 ? resolveBankName(batchBankId) : 'Multiple',
          paymentMethod: batch.paymentMethod || batchPaymentMethod || '',
          totalAmount: batch.totalAmount ?? totalPaymentAmount,
          batchStatus: batch.batchStatus || 'DRAFT',
          bankCount: batchPaymentGroups.length,
          vendorCount: batchVendorBankGroups.length,
        },
        bankSummary,
        payments: paymentsForExport,
        businessUnitName: selectedBusinessUnitName,
      };

      setLastBatchCount(batchPaymentLines.length);
      setCreatedBatchId(batch.batchNumber || batch.paymentBatchId);
      setLastBatchExport(exportPayload);

      setRows((prev) => prev
        .map((row) => {
          if (!payAmountByInvoice[row.apInvoiceId]) return row;
          const batchPayAmount = payAmountByInvoice[row.apInvoiceId];
          const balanceBefore = Number(row.balance) || 0;
          const priorPaid = Number(row.paidAmount) || 0;
          const newPaid = priorPaid + batchPayAmount;
          const newBalance = Math.max(0, balanceBefore - batchPayAmount);
          const paymentStatus = newBalance <= 0.01 ? 'PAID' : 'PARTIAL';
          return {
            ...row,
            paidAmount: newPaid,
            balance: newBalance,
            paymentStatus,
            displayPaymentStatus: formatProposalPaymentStatus(paymentStatus, newPaid, newBalance),
            raw: {
              ...row.raw,
              invoicePaymentsReceived: newPaid,
              invoiceRemainingBalanceDue: newBalance,
              paymentStatus,
            },
          };
        })
        .filter((row) => row.balance > 0 && (row.paymentStatus || '').toUpperCase() !== 'PAID'));

      setSelectionModel([]);
      setPaymentConfigs({});
      setConfirmDialogOpen(false);
      setBatchDialogOpen(true);
      showSuccess('Payment batch created successfully');
      await fetchPayableInvoices({ silent: true });
    } catch (err) {
      showError(err.message || 'Failed to create payment batch');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && rows.length === 0) {
    return (
      <Box sx={{ ...harmonatePageSx, justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: ht.white }} />
      </Box>
    );
  }

  return (
    <Box sx={harmonatePageSx}>
      <ApHubBreadcrumb variant="harmonate" />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography sx={harmonatePageTitleSx}>Payment Proposal</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={<ListAltIcon />}
            onClick={() => navigate('/treasury-payment/payment-batches')}
            sx={harmonateToolbarButtonSx}
          >
            View Payment Batches
          </Button>
          <Button
            variant="contained"
            startIcon={<PaymentIcon />}
            disabled={!selectedRows.length || submitting || !canAdd}
            onClick={handleOpenConfirm}
            sx={harmonateProceedButtonSx}
          >
            {submitting ? 'Creating...' : 'Create Payment Batch'}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0, alignItems: 'flex-start' }}>
        <Grid item xs={12} md={10} sx={{ display: 'flex', minHeight: 0, height: '100%' }}>
          <Paper
            elevation={0}
            sx={{
              ...harmonateCardSx,
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ ...harmonateSectionHeaderSx, flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ht.text }}>
                Approved Invoices ({payableRows.length})
              </Typography>
              <Typography variant="caption" sx={{ color: ht.fadedText }}>
                Select invoices, then configure bank and payment amount in the drawer before creating a batch.
              </Typography>
            </Box>
            <DataGridPro
              rows={payableRows}
              columns={columns}
              getRowId={(r) => r.apInvoiceId}
              checkboxSelection
              disableRowSelectionOnClick
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={setSelectionModel}
              onRowClick={handleRowClick}
              rowHeight={48}
              loading={loading}
              autoHeight={false}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              sx={{
                ...harmonateDataGridSx,
                flex: 1,
                minHeight: 0,
                height: '100%',
                '& .MuiDataGrid-row': { cursor: 'pointer' },
              }}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} md={2}>
          <Paper elevation={0} sx={{ ...harmonateCardSx, p: 2.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: ht.text }}>Summary</Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: ht.fadedText }}>Total Payment Amount</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: ht.green }}>
                {formatAmount(totalPaymentAmount, config.AmountFormat)}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: ht.fadedText }}>Available Cash</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, color: ht.text }}>
                {availableCash != null ? formatAmount(availableCash, config.AmountFormat) : '—'}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: ht.fadedText }}>Forecasted Balance</Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: forecastedBalance != null && forecastedBalance < 0 ? ht.red : ht.text,
                }}
              >
                {forecastedBalance != null ? formatAmount(forecastedBalance, config.AmountFormat) : '—'}
              </Typography>
            </Box>
            <Box sx={{ p: 1.5, bgcolor: ht.secondaryTab, borderRadius: '14px' }}>
              <Typography variant="caption" sx={{ color: ht.fadedText }} display="block">Selected</Typography>
              <Typography variant="body2" sx={{ color: ht.text }}>{selectedRows.length} invoice(s)</Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <PaymentProposalDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        row={activeRow}
        savedPayment={activeRow ? paymentConfigs[activeRow.apInvoiceId] : null}
        banks={banks}
        getBankDisplayName={getBankDisplayName}
        tenantId={tenantId}
        amountFormat={config.AmountFormat}
        dateFormat={config.DateFormat}
        initialForm={drawerInitialForm}
        onSave={handleSavePaymentDetails}
        saving={savingPaymentDetails}
        duplicateWarnings={warningsForInvoice(duplicateWarnings, activeRow?.apInvoiceId)}
        onOpenMatch={(apInvoiceId) => {
          const match = rows.find((item) => item.apInvoiceId === apInvoiceId);
          if (match) setActiveRow(match);
        }}
        onDismissDuplicate={handleDismissDuplicate}
        onVoidDuplicate={handleVoidDuplicate}
        duplicateActionId={duplicateActionId}
      />

      <Dialog
        open={confirmDialogOpen}
        onClose={() => !submitting && setConfirmDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '20px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, bgcolor: ht.darkBlue, color: ht.white }}>
          Create Payment Batch
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="body2">
            Review proposed payments for{' '}
            <strong>{selectedRows.length}</strong>
            {' '}
            invoice{selectedRows.length === 1 ? '' : 's'}.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total payment amount:{' '}
            <strong>{formatAmount(totalPaymentAmount, config.AmountFormat)}</strong>
            {' '}
            ({batchPaymentLines.length} payment{batchPaymentLines.length === 1 ? '' : 's'}
            {' '}
            across {batchVendorBankGroups.length} vendor{batchVendorBankGroups.length === 1 ? '' : 's'}
            {' '}
            and {batchPaymentGroups.length} bank{batchPaymentGroups.length === 1 ? '' : 's'})
          </Typography>

          <Paper variant="outlined" sx={{ overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Bank</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell align="right">Payments</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batchVendorBankGroups.map((vendor) => (
                  vendor.bankGroups.map((group, index) => (
                    <TableRow key={group.groupId}>
                      {index === 0 && (
                        <TableCell
                          rowSpan={vendor.bankGroups.length}
                          sx={{
                            fontWeight: 600,
                            verticalAlign: 'middle',
                            borderRight: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          {vendor.vendorName}
                        </TableCell>
                      )}
                      <TableCell>{resolveBankName(group.bankId) || '—'}</TableCell>
                      <TableCell>{group.paymentMethodLabel}</TableCell>
                      <TableCell align="right">{group.paymentCount}</TableCell>
                      <TableCell align="right">
                        {formatAmount(group.amount, config.AmountFormat)}
                      </TableCell>
                    </TableRow>
                  ))
                ))}
              </TableBody>
            </Table>
          </Paper>

          {selectedDuplicateWarnings.length > 0 && (
            <Alert severity="warning">
              {selectedDuplicateWarnings.map((warning) => (
                <Typography key={`${warning.paymentId}-${warning.matchedPaymentId}`} variant="body2">
                  {warningLabel(warning)}
                  {warning.matchedInvoiceNumber ? ` (${warning.invoiceNumber || 'this payment'} and ${warning.matchedInvoiceNumber})` : ''}
                </Typography>
              ))}
              <FormControlLabel
                sx={{ mt: 1 }}
                control={(
                  <Checkbox
                    checked={duplicateReviewed}
                    onChange={(event) => setDuplicateReviewed(event.target.checked)}
                  />
                )}
                label="I reviewed the possible duplicate warning"
              />
            </Alert>
          )}

          {selectedRows.some((row) => {
            const config = paymentConfigs[row.apInvoiceId];
            return config?.isPartial && Number(config.remainingBalance) > 0.01;
          }) && (
            <Alert severity="info">
              Partial invoices with remaining balance stay on the grid. Configure additional payments in the drawer for a future batch.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            disabled={submitting}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              submitting
              || !batchPaymentLines.length
              || !canAdd
              || (selectedDuplicateWarnings.length > 0 && !duplicateReviewed)
            }
            onClick={handleConfirmCreateBatch}
            sx={harmonateProceedButtonSx}
          >
            {submitting ? 'Creating...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={batchDialogOpen} onClose={() => setBatchDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogTitle sx={{ bgcolor: ht.darkBlue, color: ht.white, fontWeight: 700 }}>Payment Batch Created</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Batch: <strong>{createdBatchId}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {lastBatchCount} payment(s) queued successfully. Use Download Excel to export the batch.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (!canExport || !lastBatchExport) return;
              downloadPaymentBatchExcel(lastBatchExport);
            }}
            startIcon={<FileDownloadIcon />}
            disabled={!lastBatchExport || !canExport}
            sx={harmonateUploadOutlinedSx}
          >
            Download Excel
          </Button>
          <Button onClick={() => setBatchDialogOpen(false)} variant="outlined" sx={harmonateCancelButtonSx}>Close</Button>
        </DialogActions>
      </Dialog>

      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
      />
    </Box>
  );
};

export default PaymentProposal;
